import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { LogOut, ArrowLeft, ExternalLink, FileText, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import BlueCubeSpinner from "../components/BlueCubeSpinner";
import AdminSubNav from "../components/AdminSubNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCategory = (category) => {
  if (!category) return "-";
  const map = {
    new: "New",
    article: "Article/Paper",
    course: "Course",
    report: "Report",
    thesis: "Thesis",
    book: "Book",
    note: "Note",
    preprint: "Preprint",
  };
  return map[category.toLowerCase()] || category;
};

const normalizeOrg = (org) => {
  const raw = (org || "").trim();
  if (!raw) return "Public";
  if (raw === "PublicPublic") return "Public";
  if (raw.toLowerCase() === "public") return "Public";
  return raw;
};

const getSimilarityLabel = (percent) => {
  if (percent == null) return null;
  if (percent >= 30) return "High";
  if (percent >= 11) return "Medium";
  return "Low";
};

const AdminItemSimilarityPage = ({ user, setUser }) => {
  const { id } = useParams(); // audited item id
  const location = useLocation();
  const navigate = useNavigate();

  const [auditedItem, setAuditedItem] = useState(location.state?.auditedItem || null);
  const [matchedItem, setMatchedItem] = useState(null);
  const [percent, setPercent] = useState(location.state?.similarity?.percent ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openingFileId, setOpeningFileId] = useState(null);

  const openItemFile = async (itemId) => {
    try {
      setOpeningFileId(itemId);
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/items/${itemId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(
        new Blob([resp.data], { type: resp.headers["content-type"] || "application/pdf" }),
      );
      window.open(url, "_blank");
    } catch (err) {
      console.error("Open file error:", err);
      toast.error("Unable to open this file.");
    } finally {
      setOpeningFileId(null);
    }
  };

  const renderItemActions = (item, prefix) => (
    <div className="flex flex-wrap gap-2 pt-1">
      <Button
        variant="outline"
        size="sm"
        className="h-7 text-xs"
        onClick={() => navigate(`/admin/items?edit=${item.id}`)}
        data-testid={`${prefix}-manage-item-btn`}
      >
        <Pencil className="w-3 h-3 mr-1" /> Manage in Items
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="h-7 text-xs"
        onClick={() => window.open(`/items/${item.id}`, "_blank")}
        data-testid={`${prefix}-view-item-btn`}
      >
        <ExternalLink className="w-3 h-3 mr-1" /> View item
      </Button>
      {item.has_file && (
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          disabled={openingFileId === item.id}
          onClick={() => openItemFile(item.id)}
          data-testid={`${prefix}-open-file-btn`}
        >
          <FileText className="w-3 h-3 mr-1" />
          {openingFileId === item.id ? "Opening..." : "Open file"}
        </Button>
      )}
    </div>
  );

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/");
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("access_token");

        // If we don't have audited item details (e.g. direct URL visit), fetch it
        if (!auditedItem) {
          const itemResp = await axios.get(`${API}/items/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setAuditedItem(itemResp.data);
        }

        // If we don't have similarity details in location state, recompute
        if (percent == null || !location.state?.similarity?.matched_item_id) {
          const simResp = await axios.get(`${API}/admin/items/${id}/similarity`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const { similarity, matched_item_id } = simResp.data || {};
          if (!matched_item_id) {
            setError("No significantly similar item found.");
            setPercent(Math.round((similarity || 0) * 100));
            setMatchedItem(null);
            return;
          }
          setPercent(Math.round((similarity || 0) * 100));

          const matchResp = await axios.get(`${API}/items/${matched_item_id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setMatchedItem(matchResp.data);
        } else {
          // We know the matched item id from state
          const matchId = location.state.similarity.matched_item_id;
          setPercent(location.state.similarity.percent);

          if (matchId) {
            const matchResp = await axios.get(`${API}/items/${matchId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setMatchedItem(matchResp.data);
          }
        }
      } catch (err) {
        console.error("Similarity detail error:", err);
        setError(
          err.response?.data?.detail || "Error loading similarity comparison for this item.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const renderItemRow = (label, item) => {
    if (!item) return null;
    return (
      <tr>
        <td className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
          {label}
        </td>
        <td className="px-4 py-3">
          <div className="space-y-1">
            <div className="font-medium text-slate-900" data-testid={`${label}-title`}>
              {item.title}
            </div>
            <div className="text-xs text-slate-600">
              {item.authors && item.authors.length > 0
                ? item.authors.join(", ")
                : "No authors"}
            </div>
            <div className="text-xs text-slate-500">
              Year: {item.year || "-"}
            </div>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Navigation */}
      <nav className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1
              className="text-xl font-semibold"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Admin Dashboard
            </h1>
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate("/")}
                variant="ghost"
                className="text-white hover:text-white hover:bg-slate-800"
              >
                Public Site
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-white hover:text-white hover:bg-slate-800"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <AdminSubNav user={user} activeTab="items" />

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2
              className="text-2xl font-bold mb-1"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Similarity comparison
            </h2>
            <p className="text-xs text-slate-500 max-w-xl">
              Audited file followed by the most similar one, with similarity percentage. High
              similarity may indicate duplication or plagiarism.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/admin/items")}
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to items
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center text-sm text-slate-600">
            Loading similarity details...
            <BlueCubeSpinner />
          </div>
        ) : error ? (
          <div className="text-sm text-red-600">{error}</div>
        ) : !auditedItem ? (
          <div className="text-sm text-slate-600">Item not found.</div>
        ) : !matchedItem ? (
          <div className="text-sm text-slate-600">
            No significantly similar item found{percent != null ? ` (similarity ${percent}%)` : ""}.
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider w-40">
                    Item
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider w-32">
                    Similarity
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr>
                  <td className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Audited
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      <div
                        className="font-medium text-slate-900 cursor-pointer hover:text-orange-600 transition-colors"
                        data-testid="audited-title"
                        title="Open this file in the Items panel"
                        onClick={() => navigate(`/admin/items?edit=${auditedItem.id}`)}
                      >
                        {auditedItem.title}
                      </div>
                      <div className="text-xs text-slate-600">
                        {auditedItem.authors && auditedItem.authors.length > 0
                          ? auditedItem.authors.join(", ")
                          : "No authors"}
                      </div>
                      <div className="text-xs text-slate-500">
                        Year: {auditedItem.year || "-"} · Category: {formatCategory(auditedItem.category)} · Org: {normalizeOrg(auditedItem.organization)}
                      </div>
                      {renderItemActions(auditedItem, "audited")}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right align-middle text-xs text-slate-500">
                    —
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Similar
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      <div
                        className="font-medium text-slate-900 cursor-pointer hover:text-orange-600 transition-colors"
                        data-testid="similar-title"
                        title="Open this file in the Items panel"
                        onClick={() => navigate(`/admin/items?edit=${matchedItem.id}`)}
                      >
                        {matchedItem.title}
                      </div>
                      <div className="text-xs text-slate-600">
                        {matchedItem.authors && matchedItem.authors.length > 0
                          ? matchedItem.authors.join(", ")
                          : "No authors"}
                      </div>
                      <div className="text-xs text-slate-500">
                        Year: {matchedItem.year || "-"} · Category: {formatCategory(matchedItem.category)} · Org: {normalizeOrg(matchedItem.organization)}
                      </div>
                      {renderItemActions(matchedItem, "similar")}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right align-middle">
                    {percent != null ? (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-slate-900 text-white">
                        {getSimilarityLabel(percent)} ({percent}%)
                      </span>
                    ) : (
                      <span className="text-xs text-slate-500">-</span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminItemSimilarityPage;
